import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete, Edit } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import {
  fetchWishlist,
  updateWishlistItemStatus,
  deleteWishlistItem,
} from '../../services/dataService';
import DataTable from '../shared/DataTable';
import WishlistModal, { WishType, WishFormData } from './WishlistModal';
import ConfirmationModal from '../shared/ConfirmationModal';
import { useNotification } from '../../contexts/NotificationContext';
import { formatDateTime } from '../../utils/dateUtils';

// Define the data type for our rows
interface RowData {
  id: number;
  name: string;
  age: number;
  email: string;
  type: string; // Changed from enum to string to accommodate both types
  feedback: string;
  status: string;
  user_id: string;
  action: string;
  page_url?: string;
  user_name?: string;
  user_email?: string;
  created_at?: string;
}

// Define the type for the modal data
interface WishModalData extends WishFormData {
  id?: number;
  status?: string;
}

interface WishlistProps {
  isSystem?: boolean;
}

export const Wishlist: React.FC<WishlistProps> = ({ isSystem = false }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [rowData, setRowData] = useState<RowData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWish, setSelectedWish] = useState<WishModalData | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const columnDefs: ColDef<RowData>[] = [
    {
      field: 'created_at',
      headerName: t('wishlist.created_at'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<RowData>) => {
        return params.data?.created_at ? formatDateTime(params.data.created_at) : '-';
      },
    },
    {
      field: 'name',
      headerName: t('wishlist.name'),
      sortable: true,
      filter: true,
      cellStyle: {
        cursor: 'pointer',
        color: '#3b82f6', // blue-500 color in Tailwind
      },
      onCellClicked: (params) => {
        setSelectedWish(params.data as WishModalData);
        setIsModalOpen(true);
      },
    },
    {
      field: 'status',
      headerName: t('wishlist.status'),
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params: ValueFormatterParams<RowData>) => {
        return t(`wishlist.status_${params.data?.status}`);
      },
    },
    {
      field: 'type',
      headerName: t('wishlist.type'),
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params: ValueFormatterParams<RowData>) => {
        return t(`wishlist.type_${params.data?.type}`);
      },
    },
    {
      field: 'feedback',
      headerName: t('wishlist.feedback'),
      sortable: true,
      filter: 'agNumberColumnFilter',
      tooltipField: 'feedback',
    },
    {
      field: 'page_url',
      headerName: t('wishlist.page_url'),
      sortable: true,
      filter: true,
      tooltipField: 'page_url',
    },
    {
      field: 'user_id',
      headerName: t('wishlist.user'),
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params: ValueFormatterParams<RowData>): string => {
        return params.data?.user_name || params.data?.user_email || params.data?.user_id || '-';
      },
    },
    {
      field: 'action',
      headerName: t('wishlist.actions'),
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<RowData>) => {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => handleViewWish(params.data as RowData)}
              className="text-blue-600 hover:text-blue-800"
              title={t('wishlist.view_details')}
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDeleteConfirm(params.data?.id as number)}
              className="text-red-600 hover:text-red-800"
              title={t('wishlist.delete_wish')}
            >
              <Delete className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  ];

  const gridConfig = {
    pagination: true,
    // paginationPageSize: 50,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const response = await fetchWishlist();

      const data = response.map((item) => ({
        ...item,
        status: item.status || 'Submitted',
        type: item.type,
      }));
      setRowData(data);
    } catch (err) {
      console.error('Error fetching data:', err);
      showNotification(t('wishlist.fetch_error'), 'error');
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleViewWish = (wish: RowData) => {
    const modalData: WishModalData = {
      id: wish.id,
      name: wish.name,
      type: (wish.type as WishType) || 'other',
      feedback: wish.feedback,
      page_url: wish.page_url || window.location.pathname,
      status: wish.status,
    };
    setSelectedWish(modalData);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = (id: number) => {
    setItemToDelete(id);
    setIsConfirmDialogOpen(true);
  };

  const handleDelete = async () => {
    if (itemToDelete === null) return;

    setIsDeleting(true);
    try {
      await deleteWishlistItem(itemToDelete);
      showNotification(t('wishlist.delete_success'), 'success');
      fetchData();
    } catch (error) {
      console.error('Error deleting wishlist item:', error);
      showNotification(t('wishlist.delete_error'), 'error');
    } finally {
      setIsDeleting(false);
      setIsConfirmDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await updateWishlistItemStatus(id, newStatus);
      fetchData();
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating status:', error);
      return Promise.reject(error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">{t('sidebar.system_wishlist')}</h3>
      </div>
      <div className="bg-white rounded-lg shadow flex-grow min-h-0">
        <DataTable<RowData>
          rowData={rowData}
          columnDefs={columnDefs}
          gridConfig={gridConfig}
          isLoading={isLoading}
          height={'100%'}
          theme="ag-theme-alpine"
          noRowsMessage={t('wishlist.no_wishlist')}
        />
      </div>

      {/* View Modal */}
      {isSystem && (
        <WishlistModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedWish(null);
          }}
          wishData={selectedWish || undefined}
          isSystem={true}
          onStatusChange={handleStatusChange}
        />
      )}

      <ConfirmationModal
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('wishlist.confirm_delete')}
        message={t('wishlist.confirm_delete_message')}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Wishlist;
